import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const initial = { first_name: '', last_name: '', email: '', community_name: '', platform: 'instagram',
  profile_url: '', followers_estimate: '', community_description: '', notes: '', terms_accepted: false, marketing_consent: false };

const STATUS_LABEL = { PENDING: 'In valutazione', APPROVED: 'Approvata', REJECTED: 'Non approvata', SUSPENDED: 'Sospesa' };

export default function PartnerProgram() {
  const { user } = useAuth();
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const [form, setForm] = useState({ ...initial, email: user?.email || '', first_name: user?.name?.split(' ')[0] || '' });
  const [application, setApplication] = useState(null);
  const [portalUrl, setPortalUrl] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get('/affiliates/me').then(({ data }) => {
      setApplication(data.application);
      setPortalUrl(data.portal_url || '');
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!recaptchaSiteKey || document.querySelector('script[data-ce-recaptcha]')) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaSiteKey)}`;
    script.async = true;
    script.dataset.ceRecaptcha = 'true';
    document.head.appendChild(script);
  }, [recaptchaSiteKey]);

  const change = (key) => (event) => setForm((old) => ({ ...old, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      if (!recaptchaSiteKey || !window.grecaptcha) throw new Error('RECAPTCHA_NOT_READY');
      const recaptchaToken = await new Promise((resolve, reject) => window.grecaptcha.ready(() => {
        window.grecaptcha.execute(recaptchaSiteKey, { action: 'affiliate_application' }).then(resolve).catch(reject);
      }));
      const payload = { ...form, followers_estimate: Number(form.followers_estimate || 0), recaptcha_token: recaptchaToken };
      const { data } = await api.post('/affiliates/applications', payload);
      setApplication(data);
      setSent(true);
      toast.success('Candidatura inviata');
    } catch (error) {
      toast.error(error.message === 'RECAPTCHA_NOT_READY' ? 'Verifica anti-abuso non ancora disponibile. Riprova tra poco.' : (error.response?.data?.detail || 'Invio non riuscito'));
    } finally { setBusy(false); }
  };

  if (application || sent) {
    return <main className="page-shell py-16 max-w-3xl">
      <div className="surface p-8 text-center">
        <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={42} />
        <p className="eyebrow">Programma partner</p>
        <h1 className="font-display text-3xl font-bold text-brand-dark mt-2">{STATUS_LABEL[application?.status] || 'Candidatura ricevuta'}</h1>
        <p className="text-slate-600 mt-3">{application?.email_verification_required
          ? 'Controlla la posta e conferma il tuo indirizzo entro 24 ore. Solo dopo la conferma la candidatura passerà alla valutazione manuale.'
          : 'Riceverai gli aggiornamenti all’indirizzo indicato nella candidatura.'}</p>
        {application?.status === 'APPROVED' && <div className="mt-7 space-y-3">
          {application.referral_url && <div className="flex gap-2 max-w-xl mx-auto"><Input readOnly value={application.referral_url} />
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(application.referral_url); toast.success('Link copiato'); }} aria-label="Copia link"><Copy size={16} /></Button></div>}
          {application.promo_codes?.map((code) => <div key={code} className="flex gap-2 max-w-xl mx-auto">
            <Input readOnly value={code} aria-label="Codice personale" />
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(code); toast.success('Codice copiato'); }} aria-label="Copia codice"><Copy size={16} /></Button>
          </div>)}
          {portalUrl && <Button asChild className="rounded-full bg-brand text-white"><a href={portalUrl} target="_blank" rel="noreferrer">Apri il portale partner <ExternalLink size={15} className="ml-2" /></a></Button>}
          <p className="text-sm text-slate-600 max-w-2xl mx-auto pt-3">Quando promuovi ConcorsiEssentials tramite link o codice affiliato devi rendere chiaramente riconoscibile la natura promozionale del contenuto e rispettare i Termini del Programma Affiliati e la normativa applicabile. Questo promemoria non sostituisce una consulenza legale.</p>
        </div>}
      </div>
    </main>;
  }

  return <main>
    <section className="bg-brand-dark text-white py-16">
      <div className="page-shell max-w-4xl text-center"><p className="eyebrow text-indigo-200">Diventa Partner</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3">Aiuta la tua community a prepararsi meglio</h1>
        <p className="text-slate-300 mt-5 max-w-2xl mx-auto">Condividi ConcorsiEssentials con contenuti autentici e ricevi una commissione sulle vendite attribuite.</p></div>
    </section>
    <section className="page-shell py-14 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 max-w-6xl">
      <div><h2 className="font-display text-2xl font-bold text-brand-dark">Come funziona</h2>
        <ol className="mt-5 space-y-4 text-slate-600">
          <li><strong className="text-slate-900">1. Candidati.</strong> Raccontaci la tua community e i canali che utilizzi.</li>
          <li><strong className="text-slate-900">2. Ricevi link e codice.</strong> Dopo l’approvazione trovi il link referral, il codice personale e i risultati nel portale FirstPromoter.</li>
          <li><strong className="text-slate-900">3. Cresci.</strong> La commissione è del 15% fino a 10 clienti paganti distinti attribuiti, del 22% da 11 a 40 e del 30% da 41 in poi. Il conteggio è cumulativo, senza azzeramento mensile. Gli acquisti successivi dello stesso cliente possono generare commissioni, ma non fanno avanzare lo scaglione.</li>
        </ol>
        <div className="mt-7 rounded-2xl bg-indigo-50 p-5 text-sm text-slate-700">
          <p><strong>Vantaggio per chi ti segue:</strong> €3 di sconto, da €12,90 a €9,90.</p>
          <p className="mt-2"><strong>Per te:</strong> 15% con 1–10 clienti, 22% con 11–40 clienti, 30% da 41 clienti paganti distinti attribuiti.</p>
          <p className="mt-2">Ogni candidatura viene verificata e approvata manualmente. Dopo l’approvazione ricevi link referral e codice personale.</p>
        </div>
        <p className="text-sm text-slate-600 mt-6">La candidatura è soggetta ad approvazione. Il programma non garantisce guadagni e ogni contenuto promozionale deve essere chiaramente riconoscibile come tale.</p>
        <p className="text-xs text-slate-500 mt-6">Attribuzione, commissioni e pagamenti sono gestiti tramite FirstPromoter secondo i termini del programma.</p>
      </div>
      <form onSubmit={submit} className="surface p-6 sm:p-8 space-y-5" data-testid="affiliate-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome"><Input required minLength={2} value={form.first_name} onChange={change('first_name')} /></Field>
          <Field label="Cognome"><Input required minLength={2} value={form.last_name} onChange={change('last_name')} /></Field>
        </div>
        <Field label="Email"><Input type="email" required value={form.email} onChange={change('email')} /></Field>
        <Field label="Nome della community"><Input required value={form.community_name} onChange={change('community_name')} /></Field>
        <div className="grid sm:grid-cols-2 gap-4"><Field label="Canale principale"><select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.platform} onChange={change('platform')}>
          {['instagram','tiktok','youtube','telegram','facebook','blog','newsletter','other'].map((item) => <option key={item} value={item}>{item}</option>)}</select></Field>
          <Field label="Follower/iscritti stimati"><Input type="number" min="0" required value={form.followers_estimate} onChange={change('followers_estimate')} /></Field></div>
        <Field label="URL del profilo o sito"><Input type="url" required placeholder="https://" value={form.profile_url} onChange={change('profile_url')} /></Field>
        <Field label="Descrivi la community"><Textarea required minLength={20} value={form.community_description} onChange={change('community_description')} /></Field>
        <Field label="Note facoltative"><Textarea value={form.notes} onChange={change('notes')} /></Field>
        <label className="flex gap-3 text-sm text-slate-700"><input type="checkbox" required checked={form.terms_accepted} onChange={change('terms_accepted')} />
          <span>Ho letto e accetto i <Link className="text-brand underline" to="/termini-programma-partner" target="_blank">termini del programma partner</Link>.</span></label>
        <label className="flex gap-3 text-sm text-slate-700"><input type="checkbox" checked={form.marketing_consent} onChange={change('marketing_consent')} />
          <span>Acconsento a ricevere comunicazioni promozionali (facoltativo).</span></label>
        <Button disabled={busy} className="w-full rounded-full bg-brand hover:bg-brand-hover text-white">{busy ? 'Invio…' : 'Candidati come Partner'}</Button>
      </form>
    </section>
  </main>;
}

function Field({ label, children }) { return <div><Label>{label}</Label><div className="mt-1.5">{children}</div></div>; }
